import {useEffect, useEffectEvent} from 'react';

import {SelectProps} from '@mantine/core';
import {FormikSelect} from '@flexinet/ui-components';

import {useCustomers} from '../hooks/useCustomers.ts';
import {Customer} from '../types';
import {FieldProps} from 'formik';
import {useAuth} from "../../../hooks/useAuth.ts";

/**
 * FNP-1525: this was a Mantine `Autocomplete` with a hand-rolled `handleChange`.
 * `Autocomplete` is a free-text input — its `onChange` hands back the typed
 * LABEL string, not the option's `value` — so the id lookup FNP-1487 added
 * never matched and `setFieldValue` was never called. Picking a customer left
 * `customerId` empty and the required-field validation blocked the submit.
 *
 * `Select` passes the option `value` to `onChange`, which is the contract this
 * field needs: pick one record out of many, by id, where names are not unique.
 * `FormikSelect` from the shared library already wires that to Formik, and is
 * what `CustomersDropdown` in this same directory has always used.
 */
export const CustomersAutocomplete = (props: FieldProps & SelectProps) => {

    const {user} = useAuth()

    if (!user) {
        throw new Error('user is required parameter')
    }

    const {items: records, fetchRecords} = useCustomers();

    const fetchOnMount = useEffectEvent(() => fetchRecords());

    useEffect(() => {
        fetchOnMount();
    }, []);

    // FNP-1487: the id, never the name. Names are not unique — two customers
    // share "Ean Fisher" — and Mantine 8 throws on duplicate option values,
    // which took down the whole route.
    const selectOptions = records.map((record: Customer) => {
        return {
            value: record.id,
            label: record.name || record.id,
        };
    });

    return (
        <FormikSelect
            {...props}
            data={selectOptions}
            placeholder='Select Customer'
            disabled={records.length === 0}
        />
    );
};
